/**
 * Seeded RNG — Mulberry32 algorithm.
 * Fast, deterministic, 32-bit state. All terrain generation uses this
 * instead of Math.random() for reproducible maps.
 */

export class SeededRNG {
  private state: number;

  constructor(seed: number) {
    this.state = seed | 0;
  }

  /** Returns a float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Returns a float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Returns an integer in [min, max] (inclusive). */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** Returns true with the given probability [0, 1]. */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  /** Returns a random element from the array. */
  pick<T>(array: T[]): T {
    return array[Math.floor(this.next() * array.length)];
  }

  /** Get internal state for serialization. */
  getState(): number {
    return this.state;
  }

  /** Restore internal state from serialization. */
  setState(s: number): void {
    this.state = s | 0;
  }
}

// Module-level singleton for convenience
let rng: SeededRNG;
let currentSeed: number = 0;

/** Initialize the module RNG. If no seed provided, generates one from Date.now(). Returns the resolved seed. */
export function initRNG(seed?: number): number {
  currentSeed = seed ?? ((Date.now() * 2654435761) >>> 0);
  rng = new SeededRNG(currentSeed);
  return currentSeed;
}

export function random(): number {
  return rng.next();
}

export function randomRange(min: number, max: number): number {
  return rng.range(min, max);
}

export function randomInt(min: number, max: number): number {
  return rng.int(min, max);
}

export function randomChance(p: number): boolean {
  return rng.chance(p);
}

export function pick<T>(array: T[]): T {
  return rng.pick(array);
}

/** Get the internal RNG state for resync serialization. */
export function getRNGState(): number {
  return rng.getState();
}

/** Restore the internal RNG state from a resync snapshot. */
export function setRNGState(s: number): void {
  rng.setState(s);
}

/** Convert seed number to display string (base36, uppercase, 6 chars). */
export function seedToDisplay(seed: number): string {
  return (seed >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(-6);
}

// Initialize with a default seed immediately so calls before initRNG() don't crash
initRNG();
